import type { HttpStatus } from '@montugroup/apis-styleguide';
import type { apiSupplierContract } from '@montugroup/circuit-api-contracts';
import type { PaginationModel } from '@montugroup/design-system';
import { PaginationLabelVariant, PaginationVariant, Table } from '@montugroup/design-system';
import {
  Alert,
  AlertTitle,
  Box,
  capitalize,
  Checkbox,
  Chip,
  Container,
  debounce,
  Snackbar,
  Stack,
  TableContainer,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { CellContext, ColumnDef, Row } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import type { ClientInferResponseBody } from '@ts-rest/core';
import { DateTime } from 'luxon';
import type { FC, MouseEvent, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Illustration3 from '~/assets/svgs/illustration-3.svg';
import { Link } from '~/components/atoms/Link';
import { OrdersBanner } from '~/components/organisms/OrdersBanner';
import { OrdersTableActions } from '~/components/organisms/OrdersTable/OrdersTableActions';
import { OrdersTableToolbar } from '~/components/organisms/OrdersTable/OrdersTableToolbar';
import { FeatureFlagged } from '~/components/utilities/FeatureFlagged';
import { B2C_LEGACY_ORDERS_PATH } from '~/constants';
import { useTableManualPagination } from '~/hooks/table/useTableManualPagination';
import useTableSortingOrder from '~/hooks/table/useTableSortingOrder';
import { useSnackbar } from '~/hooks/useSnackbar';
import { Logger } from '~/lib/logger';
import { useApi } from '~/providers/ApiProvider';
import type { OrdersTableStatusType } from '~/types/OrdersTableData';
import { handlePaginationChangeBuilder } from '~/utils/handlePaginationChange';
import type { ArrayElement } from '~/utils/typscript-helpers';

const logger = new Logger('Replenishment Orders Route');
const MIN_TABLE_HEIGHT = 400;
const DEFAULT_PAGE_SIZE = 100;

type ReplenishmentOrderItemData = ClientInferResponseBody<
  typeof apiSupplierContract.listOrderItems,
  typeof HttpStatus.OK
>['data'];

export const B2COrdersV2: FC = () => {
  const { circuitApi } = useApi();
  const [status, setStatus] = useState<OrdersTableStatusType>('awaitingApproval');
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const [cursors, setCursors] = useState<{
    next: string | null;
    prev: string | null;
  }>({ prev: null, next: null });
  const {
    paginationModel: { page, pageSize },
    setPaginationModel,
    shouldResetPageIndex,
    setShouldResetPageIndex,
  } = useTableManualPagination({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const { sortingOrder, handleSortingOrderChange } = useTableSortingOrder({
    initialSortingOrder: { name: 'createdAt', reverse: true },
  });
  const [selected, setSelected] = useState<number[]>([]);
  const columnHelper = createColumnHelper<ArrayElement<ReplenishmentOrderItemData>>();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(MIN_TABLE_HEIGHT);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight;

        const FOOTER_HEIGHT = 150; // px
        const availableHeight = viewportHeight - containerTop - FOOTER_HEIGHT;

        setContainerHeight(Math.max(availableHeight, MIN_TABLE_HEIGHT));
      }
    };

    handleResize();
    const debounced = debounce(handleResize, 100);
    window.addEventListener('resize', debounced);
    return () => window.removeEventListener('resize', debounced);
  }, []);

  useEffect(() => {
    setShouldResetPageIndex(true);
  }, [setShouldResetPageIndex, sortingOrder, pageSize, status, startDate, endDate]);

  const { snackbar, updateSnackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const start = useMemo(() => startDate?.toUTC().toISO(), [startDate]);
  const end = useMemo(() => endDate?.toUTC().toISO(), [endDate]);
  const statusFilter = useMemo(() => {
    if (status === 'approved') {
      return 'APPROVED_BY_SUPPLIER';
    } else if (status === 'awaitingApproval') {
      return 'AWAITING_SUPPLIER_APPROVAL';
    } else if (status === 'rejected') {
      return 'REJECTED_BY_SUPPLIER';
    }
    return undefined;
  }, [status]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [page, pageSize, sortingOrder, statusFilter, start, end],
    queryFn: async () => {
      const res = await circuitApi.client.listOrderItems({
        body: {
          filter: {
            start: start ?? undefined,
            end: end ?? undefined,
            status: statusFilter ? [statusFilter] : undefined,
          },
          sort: [
            {
              direction: sortingOrder.reverse ? 'asc' : 'desc',
              field: sortingOrder.name as 'status' | 'createdAt',
            },
          ],
          page: {
            size: pageSize,
            after: page === 0 ? undefined : cursors.next ?? undefined,
            before: page === 0 ? undefined : cursors.prev ?? undefined,
          },
        },
      });
      if (res.status === 200) {
        return res.body;
      }
      throw new Error('Error fetching orders');
    },
  });

  useEffect(() => {
    if (isLoading) {
      setSelected([]);
    }
  }, [isLoading]);

  const responseCursors = data?.meta.page?.cursor ?? { prev: null, next: null };
  const handlePaginationChange = handlePaginationChangeBuilder({ responseCursors, setCursors });
  const onPaginationModelChange = (changedPaginationModel: PaginationModel) => {
    setPaginationModel((prevPage) => {
      handlePaginationChange({ prevPage, page: changedPaginationModel.page });
      return changedPaginationModel;
    });
    setShouldResetPageIndex(false);
  };

  // Approve/reject order items
  const { mutateAsync: mutateAsyncUpdateStatus, isPending: isPendingUpdate } =
    circuitApi.query.updateItemSupplierApprovalContact.useMutation({
      onSuccess: () => {
        showSnackbar('Order item status updated', 'success');
        refetch();
      },
      onError: (error) => {
        logger.error('Error updating order item status', error);
        showSnackbar('Error updating order item status', 'error');
      },
    });

  const handleCloseSnackbar = () => {
    closeSnackbar();
    setTimeout(() => updateSnackbar(''), 500); // Delay to allow the snackbar to close
  };

  const handleBatchUpdateStatus = async (ids: number[], approvalStatus: 'APPROVED' | 'REJECTED') => {
    await Promise.all(
      ids.map((id) =>
        mutateAsyncUpdateStatus({
          params: { replenishmentOrderItemId: id },
          body: { data: { approvalStatus } },
        }),
      ),
    );
  };

  useEffect(() => {
    if (isError) {
      const errorMessage = 'Failed to fetch orders';
      logger.error(errorMessage, error);
      showSnackbar(errorMessage, 'error');
    }
  }, [error, isError, showSnackbar]);

  const columns = useMemo((): ColumnDef<ArrayElement<ReplenishmentOrderItemData>>[] => {
    const defaultCell = (opts: {
      accessorKey: string;
      header: string;
      enableSorting: boolean;
      value: (cell: CellContext<ArrayElement<ReplenishmentOrderItemData>, unknown>) => ReactNode;
    }) => {
      return columnHelper.accessor(opts.accessorKey as any, {
        header: () => (
          <Typography component="span" variant="caption" whiteSpace="nowrap">
            {opts.header}
          </Typography>
        ),
        enableSorting: opts.enableSorting,
        cell: (cell) => {
          return (
            <Typography component="span" variant="caption" fontWeight={400}>
              {opts.value(cell)}
            </Typography>
          );
        },
      });
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.checked) {
        return setSelected([]);
      }

      if (!data?.data) {
        return;
      }

      const newSelected = data.data
        .filter((row) => row.status === 'AWAITING_SUPPLIER_APPROVAL')
        .map((row) => row.replenishmentOrderItemId);
      setSelected(newSelected);
    };

    const handleClick = (_: MouseEvent<HTMLButtonElement>, id: number) => {
      if (!data?.data) {
        return;
      }

      setSelected((prevSelected) => {
        return prevSelected.includes(id)
          ? prevSelected.filter((selectedId) => selectedId !== id)
          : [...prevSelected, id];
      });
    };

    return [
      {
        accessorKey: 'selected',
        enableSorting: false,
        header: () => {
          const awaitingApproval =
            data?.data.map((row) => row.status).filter((status) => status === 'AWAITING_SUPPLIER_APPROVAL') ?? [];
          const isEnabled = awaitingApproval.length > 0;
          return (
            <Checkbox
              size="medium"
              data-testid="orders-table-select-all-checkbox"
              onChange={handleSelectAllClick}
              disabled={!isEnabled}
              checked={data && data.data.length > 0 && selected.length === awaitingApproval.length && isEnabled}
              inputProps={{ 'aria-labelledby': `replenishment-orders-table-select-all-checkbox` }}
              sx={{
                cursor: isEnabled ? 'pointer' : 'default',
                pointerEvents: isEnabled ? 'auto' : 'none',
                opacity: isEnabled ? 1 : 0.5,
              }}
            />
          );
        },
        cell: ({ row }) => {
          const isDisabled = row.original.status !== 'AWAITING_SUPPLIER_APPROVAL';
          return (
            <Checkbox
              onClick={!isDisabled ? (event) => handleClick(event, row.original.replenishmentOrderItemId) : undefined}
              checked={selected.includes(row.original.replenishmentOrderItemId)}
              inputProps={{ 'aria-labelledby': `replenishment-orders-table-checkbox-${row.index}` }}
              sx={{
                cursor: isDisabled ? 'default' : 'pointer',
                pointerEvents: isDisabled ? 'none' : 'auto',
                opacity: isDisabled ? 0.5 : 1,
              }}
            />
          );
        },
      },
      defaultCell({
        accessorKey: 'productName',
        header: 'Product Name',
        enableSorting: false,
        value: ({ row }) => row.original.product.name,
      }),
      defaultCell({
        accessorKey: 'productSku',
        header: 'Product SKU',
        enableSorting: false,
        value: ({ row }) => row.original.product.sku,
      }),
      defaultCell({
        accessorKey: 'quantity',
        header: 'Quantity',
        enableSorting: false,
        value: ({ row }) => row.original.quantity,
      }),
      defaultCell({
        accessorKey: 'createdAt',
        header: 'Order Date',
        enableSorting: true,
        value: ({ row }) => row.original.createdDate && DateTime.fromISO(row.original.createdDate).toFormat('d/MMM/yy'),
      }),
      defaultCell({
        accessorKey: 'pharmacyName',
        header: 'Pharmacy',
        enableSorting: false,
        value: ({ row }) => row.original.pharmacy.name,
      }),
      defaultCell({
        accessorKey: 'address',
        header: 'Address',
        enableSorting: false,
        value: ({ row }) => row.original.pharmacy.address,
      }),
      defaultCell({
        accessorKey: 'contact',
        header: 'Contact',
        enableSorting: false,
        value: ({ row }) => row.original.pharmacy.phone,
      }),
      defaultCell({
        accessorKey: 'approval',
        header: 'Approval',
        enableSorting: false,
        value: ({ row }) =>
          row.original.approval ? (
            <Link id="b2b_table_cell_document_link" to={row.original.approval.documentPath}>
              {row.original.approval.code}
            </Link>
          ) : (
            ''
          ),
      }),
      defaultCell({
        accessorKey: 'prescriber',
        header: 'Prescriber Name',
        enableSorting: false,
        value: ({ row }) => row.original.approval?.prescriberName,
      }),
      {
        accessorKey: 'status',
        header: () => (
          <Typography component="span" variant="caption" whiteSpace="nowrap">
            Status
          </Typography>
        ),
        enableSorting: false,
        cell: ({ row }) => {
          let chipColor: 'success' | 'error' | 'warning' = 'warning';
          let text: string = 'Awaiting Approval';
          switch (row.original.status) {
            case 'APPROVED_BY_SUPPLIER':
              chipColor = 'success';
              text = 'Approved';
              break;
            case 'REJECTED_BY_SUPPLIER':
              chipColor = 'error';
              text = 'Rejected';
              break;
          }
          return <Chip label={text} color={chipColor} />;
        },
      },
    ];
  }, [selected, data, columnHelper]);

  return (
    <>
      <Snackbar
        open={snackbar.isOpen && snackbar.message !== ''}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.type} sx={{ width: '100%' }}>
          {snackbar.type && <AlertTitle>{capitalize(snackbar.type)}</AlertTitle>}
          {snackbar.message}
        </Alert>
      </Snackbar>

      <OrdersBanner
        imageUrl={Illustration3}
        isLoading={isLoading}
        title="Orders"
        subtitle="Alternaleaf & Circuit system orders."
        count={null}
      />

      <FeatureFlagged featureFlag="expose_legacy_orders">
        <Container data-testid="legacy-orders-alert">
          <Box mt={2}>
            <Alert variant="standard" severity="info">
              <h3>
                Legacy order (orders placed before 6th Nov requiring approvals) will be available for short period.
              </h3>
              <p>Click the link below to check if any legacy orders required your approval</p>
              <Link to={B2C_LEGACY_ORDERS_PATH}>View legacy orders</Link>
            </Alert>
          </Box>
        </Container>
      </FeatureFlagged>

      <Container data-testid="orders-table">
        <Box py={{ xs: 3.5, md: 5 }}>
          <Stack spacing={{ xs: 4, md: 5 }}>
            <OrdersTableToolbar
              status={status}
              setStatus={setStatus}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              searchOpts={null}
              exportOpts={null}
              type="replenishment"
            />

            <TableContainer ref={containerRef}>
              <Table
                skeletonCount={DEFAULT_PAGE_SIZE}
                hasRowBackgroundColor
                isLoading={isLoading}
                data={data?.data ?? []}
                columns={columns}
                showPagination
                manualPagination
                paginationLabelVariant={PaginationLabelVariant.NONE}
                paginationVariant={PaginationVariant.VARIABLE_PAGE_SIZE}
                isScrollable
                maxHeight={containerHeight}
                hasNextPage={!!data?.meta.page?.cursor.next}
                hasPrevPage={!!data?.meta.page?.cursor.prev}
                pageSize={DEFAULT_PAGE_SIZE}
                initialSort={[{ id: 'createdAt', desc: true }]}
                shouldResetPageIndex={shouldResetPageIndex}
                onPaginationModelChange={onPaginationModelChange}
                onSortingChange={handleSortingOrderChange}
                applyRowStyles={(row) => {
                  const replenishmentOrderItemRow = row as Row<ArrayElement<ReplenishmentOrderItemData>>;
                  const isSelected = selected.includes(replenishmentOrderItemRow.original.replenishmentOrderItemId);
                  let statusColor: string;
                  switch (replenishmentOrderItemRow.original.status) {
                    case 'APPROVED_BY_SUPPLIER':
                      statusColor = 'success.main';
                      break;
                    case 'REJECTED_BY_SUPPLIER':
                      statusColor = 'error.main';
                      break;
                    default:
                      statusColor = 'warning.main';
                      break;
                  }
                  return {
                    height: 100,
                    backgroundColor: 'transparent',
                    '& td:first-of-type': {
                      border: 'none',
                      borderLeft: '3px solid',
                      borderColor: statusColor,
                    },
                    '&:nth-of-type(odd) td': { backgroundColor: 'grey.50' },
                    '&:nth-of-type(even) td': { backgroundColor: 'background.paper' },
                    ...(isSelected && {
                      '& td:nth-of-type(odd), & td:nth-of-type(even)': {
                        backgroundColor: 'action.selected',
                      },
                    }),
                    '&:hover td:nth-of-type(odd), &:hover td:nth-of-type(even)': {
                      backgroundColor: 'action.hover',
                    },
                  };
                }}
              />
            </TableContainer>
          </Stack>

          <OrdersTableActions
            selected={selected}
            setSelected={setSelected}
            handleApproveItems={(ids: number[]) => handleBatchUpdateStatus(ids, 'APPROVED')}
            approveLoading={isPendingUpdate}
            handleRejectItems={(ids: number[]) => handleBatchUpdateStatus(ids, 'REJECTED')}
            rejectLoading={isPendingUpdate}
            type="replenishment"
          />
        </Box>
      </Container>
    </>
  );
};
